import { memo } from 'react';
import { AlertTriangle, Clock, Globe, RotateCcw } from 'lucide-react';
import { cn } from '../../utils/cn';
import {
  HEAVY_POINT_THRESHOLD,
  NORMALIZATION_DESCRIPTIONS,
  NORMALIZATION_LABELS,
  RANGE_PRESETS,
  formatDateTimeInput,
  parseDateTimeInput,
} from '../../utils/starchart';
import type { StarchartRangeState } from '../../hooks/useStarchartRange';
import type { NormalizationMode, StarchartResolution } from '../../types/starchart';

const RESOLUTIONS: { value: StarchartResolution; label: string }[] = [
  { value: 'auto', label: 'Auto' },
  { value: 'raw', label: 'Raw (1m)' },
  { value: '15m', label: '15 minutes' },
  { value: 'hourly', label: 'Hourly' },
];

const NORMALIZATIONS: NormalizationMode[] = [
  'none',
  'moving-average',
  'min-max',
  'z-score',
  'percentage-change',
];

interface StarchartToolbarProps {
  range: StarchartRangeState;
  /** Normalisation only makes sense where series are compared. */
  showNormalization?: boolean;
  className?: string;
}

/**
 * Range, resolution and display controls shared by both StarChart views.
 *
 * Times render in the viewer's zone by default, matching the rest of ARGUS, with a UTC toggle for
 * comparing notes across regions. The standalone site was UTC-only, which meant reading a chart
 * always involved arithmetic.
 */
export const StarchartToolbar = memo(
  ({ range, showNormalization = false, className }: StarchartToolbarProps) => {
    const heavy = range.estimatedPoints > HEAVY_POINT_THRESHOLD;

    return (
      <div className={cn('flex flex-col gap-2', className)}>
        <div className="flex flex-col md:flex-row md:items-center gap-2">
          <div className="segmented" role="group" aria-label="Time range">
            {RANGE_PRESETS.map((preset) => (
              <button
                key={preset.id}
                type="button"
                className="segmented-item"
                data-active={range.presetId === preset.id}
                onClick={() => range.setPreset(preset.id)}
              >
                {preset.label}
              </button>
            ))}
          </div>

          {range.isZoomed && (
            <button
              type="button"
              className="glass-button-sm"
              onClick={range.resetZoom}
              title="Return to the default range"
            >
              <RotateCcw className="w-4 h-4" />
              Reset zoom
            </button>
          )}

          <div className="md:ml-auto flex items-center gap-2">
            <div className="segmented" role="group" aria-label="Time zone">
              <button
                type="button"
                className="segmented-item segmented-item-sm"
                data-active={range.timeZone === 'local'}
                onClick={() => range.setTimeZone('local')}
                title="Show times in your local time zone"
              >
                <Clock className="w-3.5 h-3.5" />
                Local
              </button>
              <button
                type="button"
                className="segmented-item segmented-item-sm"
                data-active={range.timeZone === 'utc'}
                onClick={() => range.setTimeZone('utc')}
                title="Show times in UTC"
              >
                <Globe className="w-3.5 h-3.5" />
                UTC
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          <div>
            <label
              className="block text-xs font-medium text-glass-400 mb-1.5"
              htmlFor="starchart-from"
            >
              From ({range.timeZone === 'utc' ? 'UTC' : 'local'})
            </label>
            <input
              id="starchart-from"
              type="datetime-local"
              className="form-input"
              value={formatDateTimeInput(range.start, range.timeZone)}
              onChange={(e) => {
                const parsed = parseDateTimeInput(e.target.value, range.timeZone);
                if (parsed) range.setStart(parsed);
              }}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-glass-400 mb-1.5" htmlFor="starchart-to">
              To ({range.timeZone === 'utc' ? 'UTC' : 'local'})
            </label>
            <input
              id="starchart-to"
              type="datetime-local"
              className="form-input"
              value={formatDateTimeInput(range.end, range.timeZone)}
              onChange={(e) => {
                const parsed = parseDateTimeInput(e.target.value, range.timeZone);
                if (parsed) range.setEnd(parsed);
              }}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-glass-400 mb-1.5" htmlFor="starchart-res">
              Resolution
            </label>
            <select
              id="starchart-res"
              className="form-input"
              value={range.resolutionPreference}
              onChange={(e) => range.setResolution(e.target.value as StarchartResolution)}
            >
              {RESOLUTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-glass-500 mt-1 tabular-nums">
              {range.resolutionPreference === 'auto'
                ? `Auto picked ${range.resolution} — about ${range.estimatedPoints.toLocaleString()} samples`
                : `About ${range.estimatedPoints.toLocaleString()} samples`}
            </p>
          </div>

          {showNormalization && (
            <div>
              <label
                className="block text-xs font-medium text-glass-400 mb-1.5"
                htmlFor="starchart-norm"
              >
                Normalisation
              </label>
              <select
                id="starchart-norm"
                className="form-input"
                value={range.normalization}
                onChange={(e) => range.setNormalization(e.target.value as NormalizationMode)}
              >
                {NORMALIZATIONS.map((mode) => (
                  <option key={mode} value={mode}>
                    {NORMALIZATION_LABELS[mode]}
                  </option>
                ))}
              </select>
              <p className="text-xs text-glass-500 mt-1">
                {NORMALIZATION_DESCRIPTIONS[range.normalization]}
              </p>
            </div>
          )}
        </div>

        {range.normalization === 'moving-average' && showNormalization && (
          <div className="max-w-xs">
            <label className="block text-xs font-medium text-glass-400 mb-1.5" htmlFor="starchart-ma">
              Smoothing window
            </label>
            <input
              id="starchart-ma"
              type="number"
              min={3}
              max={51}
              step={2}
              className="form-input"
              value={range.movingAverageWindow}
              onChange={(e) => range.setMovingAverageWindow(Number(e.target.value) || 5)}
            />
            <p className="text-xs text-glass-500 mt-1">Samples averaged around each point.</p>
          </div>
        )}

        {heavy && (
          <div className="alert alert-warning">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span>
              This range asks for roughly {range.estimatedPoints.toLocaleString()} samples. A coarser
              resolution loads far faster and looks the same at this width.
            </span>
          </div>
        )}
      </div>
    );
  },
);

StarchartToolbar.displayName = 'StarchartToolbar';
